import React, { useMemo } from 'react';
import { useNavigate, Routes, Route } from 'react-router-dom';
import { FaClipboardList } from '@react-icons/all-files/fa/FaClipboardList';
import { FaBuilding } from '@react-icons/all-files/fa/FaBuilding';
import { FaConciergeBell } from '@react-icons/all-files/fa/FaConciergeBell';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaShieldAlt } from '@react-icons/all-files/fa/FaShieldAlt';
import { FaUsers } from '@react-icons/all-files/fa/FaUsers';
import { FaCog } from '@react-icons/all-files/fa/FaCog';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import Breadcrumb from '../commons/Breadcrumb';
import TileContainer, { TileItem } from '../commons/TileContainer';

import ActividadesConfigPage from './configuracion/actividades/actividades-page';
import EmpresaConfigPage     from './configuracion/empresa/empresa-page';
import ServiciosConfigPage   from './configuracion/servicios/servicios-page';
import WorkflowConfigPage    from './configuracion/workflow/workflow-page';
import CalendarioConfigPage  from './configuracion/calendario/calendario-page';
import UsuariosConfigPage    from './configuracion/usuarios/usuarios-page';
import SeguridadConfigPage   from './configuracion/seguridad/seguridad-page';
import RequireAuthorities from '../shared/RequireAuthorities';
import { useAppSelector } from '../store';
import { canAccessUsuariosConfiguracion, USUARIOS_CONFIGURACION_REQUIRED_AUTHORITIES } from '../shared/authorization';

const allTiles: TileItem[] = [
  { key: 'actividades', label: 'Actividades', icon: <FaClipboardList />, color: '#3b82f6' },
  { key: 'empresa',     label: 'Empresa',     icon: <FaBuilding />,      color: '#8b5cf6' },
  { key: 'servicios',   label: 'Servicios',   icon: <FaConciergeBell />, color: '#f59e0b' },
  { key: 'workflow',    label: 'Workflow',    icon: <FaProjectDiagram />, color: '#10b981' },
  { key: 'calendario',  label: 'Calendario',  icon: <FaCalendarAlt />,   color: '#ef4444' },
  { key: 'seguridad',   label: 'Seguridad',   icon: <FaShieldAlt />,       color: '#475569' },
  { key: 'usuarios',    label: 'Usuarios',    icon: <FaUsers />,         color: '#0ea5e9' },
];

function ConfiguracionIndex() {
  const navigate = useNavigate();
  const account = useAppSelector(s => s.authentication.account);
  const tiles = useMemo(
    () =>
      canAccessUsuariosConfiguracion(account?.authorities)
        ? allTiles
        : allTiles.filter(t => t.key !== 'usuarios' && t.key !== 'seguridad'),
    [account],
  );

  return (
    <div style={s.page}>
      <Breadcrumb items={[
        { label: 'Inicio',        to: '/',   icon: <FaHome /> },
        { label: 'Configuración',            icon: <FaCog />  },
      ]} />

      <div style={s.header}>
        <h2 style={s.title}>Configuración</h2>
        <p style={s.subtitle}>Administra las opciones del sistema</p>
      </div>

      <TileContainer
        tiles={tiles}
        onSelect={key => navigate(`/configuracion/${key}`)}
      />
    </div>
  );
}

export default function Configuracion() {
  return (
    <Routes>
      <Route path="/"             element={<ConfiguracionIndex />} />
      <Route path="/actividades/*" element={<ActividadesConfigPage />} />
      <Route path="/empresa/*"     element={<EmpresaConfigPage />} />
      <Route path="/servicios/*"   element={<ServiciosConfigPage />} />
      <Route path="/workflow/*"    element={<WorkflowConfigPage />} />
      <Route path="/calendario"    element={<CalendarioConfigPage />} />
      <Route path="/calendario/*"  element={<CalendarioConfigPage />} />
      <Route
        path="/seguridad"
        element={
          <RequireAuthorities requiredAuthorities={USUARIOS_CONFIGURACION_REQUIRED_AUTHORITIES}>
            <SeguridadConfigPage />
          </RequireAuthorities>
        }
      />
      <Route
        path="/seguridad/*"
        element={
          <RequireAuthorities requiredAuthorities={USUARIOS_CONFIGURACION_REQUIRED_AUTHORITIES}>
            <SeguridadConfigPage />
          </RequireAuthorities>
        }
      />
      <Route
        path="/usuarios/*"
        element={
          <RequireAuthorities requiredAuthorities={USUARIOS_CONFIGURACION_REQUIRED_AUTHORITIES}>
            <UsuariosConfigPage />
          </RequireAuthorities>
        }
      />
    </Routes>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:    { display: 'flex', flexDirection: 'column', gap: 28 },
  header:  { display: 'flex', flexDirection: 'column', gap: 4 },
  title:   { margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' },
  subtitle:{ margin: 0, fontSize: 13, color: '#64748b' },
};
